import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Stack,
  Typography,
  Card,
  Grid,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Chip,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ChevronLeft,
  Wifi,
  Phone,
  Tv,
  Zap as ElectricityIcon,
  GraduationCap,
  Sparkles,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Page from '../components/Page';
import {
  createNewProduct,
  updateProduct,
  getServiceProviders,
  getAppSetting,
} from '../api/services';
import { BRAND_IMAGES } from '../utils/assets';
import MultiProcessorRoutingCard from './components/Services/MultiProcessorRoutingCard';

// Service definitions
const SERVICES_LIST = [
  {
    key: 'Data',
    title: 'Data bundles',
    icon: Wifi,
    color: '#0066FE',
    bg: '#EFF6FF',
    defaultValidity: '30 Days',
    presetCategories: [
      'MTN SME',
      'MTN CG',
      'MTN Direct Coupon',
      'AIRTEL CG',
      'AIRTEL SME',
      'GLO SME',
      'GLO Direct',
      '9MOBILE SME',
    ],
  },
  {
    key: 'Airtime',
    title: 'Airtime VTU',
    icon: Phone,
    color: '#16A34A',
    bg: '#DCFCE7',
    defaultValidity: 'Instant VTU',
    presetCategories: ['MTN', 'AIRTEL', 'GLO', '9MOBILE'],
  },
  {
    key: 'Cable',
    title: 'Cable TV',
    icon: Tv,
    color: '#D97706',
    bg: '#FEF3C7',
    defaultValidity: 'Monthly',
    presetCategories: ['DSTV', 'GOTV', 'STARTIMES', 'SHOWMAX'],
  },
  {
    key: 'Electricity',
    title: 'Electricity bills',
    icon: ElectricityIcon,
    color: '#EA580C',
    bg: '#FFEDD5',
    defaultValidity: 'Instant Token',
    presetCategories: [
      'Jos Electricity',
      'Ikeja Electricity',
      'Eko Electricity',
      'Abuja Electricity',
      'Ibadan Electricity',
      'Enugu Electricity',
      'Kano Electricity',
      'Port Harcourt Electricity',
      'Benin Electricity',
      'Kaduna Electricity',
      'Yola Electricity',
    ],
  },
  {
    key: 'ResultCheck',
    title: 'Result checkers',
    icon: GraduationCap,
    color: '#7C3AED',
    bg: '#EDE9FE',
    defaultValidity: 'Instant e-PIN',
    presetCategories: ['WAEC', 'NECO', 'NABTEB'],
  },
];



// Robust brand logo finder
const getBrandLogo = (category, details) => {
  const text = `${category || ''} ${details?.name || ''}`
    .toLowerCase()
    .replace(/[\s\-_]/g, '');
  const matchedKey = Object.keys(BRAND_IMAGES).find((key) => text.includes(key));
  return matchedKey ? BRAND_IMAGES[matchedKey] : null;
};

export default function ProductAddPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Query parameter to pre-select category (e.g. ?service=data or ?service=airtime)
  const queryParams = new URLSearchParams(location.search);
  const serviceParamRaw = queryParams.get('service') || queryParams.get('category');
  const hasServiceParam = Boolean(serviceParamRaw && serviceParamRaw.trim());
  const initialServiceParam = (serviceParamRaw || 'Data').toLowerCase();

  const matchedInitial = SERVICES_LIST.find(
    (s) => s.key.toLowerCase() === initialServiceParam
  );

  const [selectedService, setSelectedService] = useState(
    matchedInitial ? matchedInitial.key : 'Data'
  );

  // If user navigated directly from a specific service page (e.g. Airtime Service), scope directly to it
  const [isScopedService, setIsScopedService] = useState(
    Boolean(hasServiceParam && matchedInitial)
  );

  const activeServiceConfig = useMemo(
    () => SERVICES_LIST.find((s) => s.key === selectedService) || SERVICES_LIST[0],
    [selectedService]
  );

  // Selected Network for telecom services (MTN, Airtel, Glo, 9mobile)
  const [selectedDataNetwork, setSelectedDataNetwork] = useState('MTN');

  const initialCategory = useMemo(() => {
    if (matchedInitial) {
      if (matchedInitial.key === 'Airtime') return 'MTN';
      if (matchedInitial.key === 'Data') return 'MTN SME';
      return matchedInitial.presetCategories[0] || '';
    }
    return 'MTN SME';
  }, [matchedInitial]);

  const initialName = useMemo(() => {
    if (matchedInitial?.key === 'Airtime') return 'MTN Airtime VTU';
    return '';
  }, [matchedInitial]);

  const [providers, setProviders] = useState([]);
  const [currentProcessors, setCurrentProcessors] = useState([]);
  const [selectedProcessor, setSelectedProcessor] = useState('');
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: initialName,
    category: initialCategory,
    amount: '',
    vipAmount: '',
    buyingAmount: '',
    discount: 0,
    fee: 0,
    cashback: 0,
    referralBonus: 0,
    validity: (matchedInitial || SERVICES_LIST[0]).defaultValidity,
    descriptions: '',
    planId: '',
    planNetworkId: '1',
    available: true,
    hidden: false,
  });

  // V2 Router State
  const [useV2Engine, setUseV2Engine] = useState(true);
  const [processorRoutes, setProcessorRoutes] = useState([]);

  // Fetch providers and app processors
  const fetchProcessors = useCallback(async () => {
    setLoadingInitial(true);
    try {
      const [provRes, appRes] = await Promise.all([
        getServiceProviders(),
        getAppSetting(),
      ]);

      if (provRes.ok && Array.isArray(provRes.data?.data)) {
        setProviders(provRes.data.data);
      }
      if (appRes.ok && appRes.data?.data) {
        const appSetting = appRes.data.data;
        const cur = appSetting.service_processor?.current_processors || [];
        setCurrentProcessors(cur);
        if (cur.length > 0) {
          setSelectedProcessor(cur[0].identifier);
        }
      }
    } catch (err) {
      toast.error('Failed to load service processors');
    } finally {
      setLoadingInitial(false);
    }
  }, []);

  useEffect(() => {
    fetchProcessors();
  }, [fetchProcessors]);

  // Handle switching Data / Airtime Network (MTN, Airtel, Glo, 9mobile)
  const handleDataNetworkChange = (networkKey, networkId) => {
    setSelectedDataNetwork(networkKey);
    setProcessorRoutes((prevRoutes) =>
      prevRoutes.map((r) => {
        if (r.isCustomId) return r;
        return {
          ...r,
          planId: '',
          costPrice: undefined,
          catalogPlan: null,
        };
      })
    );
    setFormData((prev) => {
      let matchingPreset = activeServiceConfig.presetCategories.find((c) =>
        c.toUpperCase().startsWith(networkKey.toUpperCase())
      );
      if (!matchingPreset) {
        matchingPreset = selectedService === 'Airtime' ? networkKey : `${networkKey} SME`;
      }

      let newName = prev.name;
      if (selectedService === 'Airtime') {
        newName = `${networkKey} Airtime VTU`;
      }

      return {
        ...prev,
        category: matchingPreset,
        name: newName,
        planNetworkId: networkId || '1',
      };
    });
  };

  // Scoped preset categories for Section 2
  const visiblePresets = useMemo(() => {
    if ((selectedService === 'Data' || selectedService === 'Airtime') && selectedDataNetwork) {
      const filtered = activeServiceConfig.presetCategories.filter((preset) =>
        preset.toUpperCase().startsWith(selectedDataNetwork.toUpperCase())
      );
      if (filtered.length > 0) return filtered;
    }
    return activeServiceConfig.presetCategories;
  }, [selectedService, selectedDataNetwork, activeServiceConfig]);

  // When changing service tab, adjust defaults
  const handleServiceChange = (serviceKey) => {
    setSelectedService(serviceKey);
    const cfg = SERVICES_LIST.find((s) => s.key === serviceKey);
    if (cfg) {
      let defaultCategory = cfg.presetCategories[0] || '';
      let defaultNetId = '1';
      let defaultName = '';

      if (serviceKey === 'Data') {
        const netPreset =
          cfg.presetCategories.find((c) =>
            c.toUpperCase().startsWith(selectedDataNetwork.toUpperCase())
          ) || cfg.presetCategories[0];
        defaultCategory = netPreset;
        const netIdMap = { MTN: '1', GLO: '2', '9MOBILE': '3', AIRTEL: '4' };
        defaultNetId = netIdMap[selectedDataNetwork] || '1';
      } else if (serviceKey === 'Airtime') {
        defaultCategory = selectedDataNetwork || 'MTN';
        defaultName = `${defaultCategory} Airtime VTU`;
        const netIdMap = { MTN: '1', GLO: '2', '9MOBILE': '3', AIRTEL: '4' };
        defaultNetId = netIdMap[selectedDataNetwork] || '1';
      }

      setFormData((prev) => ({
        ...prev,
        category: defaultCategory,
        validity: cfg.defaultValidity,
        name: defaultName,
        planNetworkId: defaultNetId,
      }));
    }
  };

  // Sync plan info callback from MultiProcessorRoutingCard
  const handleSyncPlan = ({ plan, isManualSync }) => {
    if (!plan) return;
    setFormData((prev) => ({
      ...prev,
      planId: String(plan.planId),
      buyingAmount: (!prev.buyingAmount || isManualSync) ? String(plan.costPrice || 0) : prev.buyingAmount,
      name: (!prev.name || isManualSync) ? (plan.planName || prev.name) : prev.name,
      validity: (!prev.validity || isManualSync) ? (plan.validity || prev.validity) : prev.validity,
    }));
  };

  // Profit Margin Live Calculations
  const isAirtime = selectedService === 'Airtime';
  const isElectricity = selectedService === 'Electricity';

  const discountNum = Number(formData.discount || 0);
  const buyingNum = Number(formData.buyingAmount || 0);
  const vipAirtimeDiscount = Number(formData.vipAmount || 0);
  const airtimeMargin = buyingNum - discountNum;
  const airtimeVipMargin = buyingNum - vipAirtimeDiscount;

  const retailAmount = Number(formData.amount || 0);
  const wholesaleCost = Number(formData.buyingAmount || 0);
  const vipPrice = Number(formData.vipAmount || 0);
  const cashbackAmount = Number(formData.cashback || 0);
  const referralAmount = Number(formData.referralBonus || 0);

  // Accurate Cash Economics
  const grossMargin = retailAmount - wholesaleCost;
  const totalRewards = cashbackAmount + referralAmount;
  const totalExpenses = wholesaleCost + totalRewards;
  const netMargin = retailAmount - totalExpenses;
  const netMarginPercent = retailAmount > 0 ? (netMargin / retailAmount) * 100 : 0;

  // VIP Pricing & Margin (if VIP price is entered)
  const hasVipPrice = vipPrice > 0;
  const vipNetMargin = hasVipPrice ? vipPrice - totalExpenses : null;
  const vipNetMarginPercent = hasVipPrice ? (vipNetMargin / vipPrice) * 100 : 0;

  // Submit Handler
  const handleCreateProduct = async () => {
    if (!formData.category.trim()) {
      toast.error('Please enter or select a category / network.');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Please enter a product or plan name.');
      return;
    }

    if (!isAirtime && !formData.amount && !isElectricity) {
      toast.error('Please enter a retail selling price.');
      return;
    }

    setSaving(true);
    try {
      const brandLogo = getBrandLogo(formData.category, { name: formData.name });
      const activeProcessorSlug =
        processorRoutes[0]?.processorSlug ||
        selectedProcessor ||
        currentProcessors[0]?.identifier ||
        'smeplug';

      let requestPayload = {
        service: selectedService,
        category: formData.category.trim(),
        available: formData.available,
        hidden: formData.hidden,
        validity: formData.validity,
        descriptions: formData.descriptions,
        imageUrl: brandLogo || '',
        planId: formData.planId || processorRoutes[0]?.planId || '',
        useV2Engine,
        processorRoutes,
      };

      if (isAirtime) {
        requestPayload = {
          ...requestPayload,
          category: formData.category.toUpperCase().trim(),
          amount: 0,
          discount: Number(formData.discount || 0),
          buyingAmount: Number(formData.buyingAmount || 0),
          vipAmount: Number(formData.vipAmount || 0),
          fee: Number(formData.fee || 0),
          cashback: Number(formData.cashback || 0),
          referralBonus: Number(formData.referralBonus || 0),
          details: {
            name: formData.name.trim() || `${formData.category} Airtime VTU`,
          },
        };
      } else if (selectedService === 'Data') {
        requestPayload = {
          ...requestPayload,
          category: formData.category.toUpperCase().trim(),
          amount: Number(formData.amount || 0),
          buyingAmount: Number(formData.buyingAmount || 0),
          vipAmount: Number(formData.vipAmount || 0),
          cashback: Number(formData.cashback || 0),
          referralBonus: Number(formData.referralBonus || 0),
          details: {
            name: formData.name.trim(),
            planName: formData.planId || formData.name.trim(),
            amount: Number(formData.amount || 0),
            custom: true,
          },
          customData: {
            networkid: formData.planNetworkId || '1',
            planid: formData.planId || '1001',
          },
        };
      } else {
        requestPayload = {
          ...requestPayload,
          amount: Number(formData.amount || 0),
          buyingAmount: Number(formData.buyingAmount || 0),
          vipAmount: Number(formData.vipAmount || 0),
          fee: Number(formData.fee || 0),
          cashback: Number(formData.cashback || 0),
          referralBonus: Number(formData.referralBonus || 0),
          details: {
            name: formData.name.trim(),
            planName: formData.planId || formData.name.trim(),
          },
        };
      }

      // Step 1: Create Product
      const createRes = await createNewProduct(requestPayload, activeProcessorSlug);

      if (createRes.ok) {
        const createdService = createRes.data?.data;
        const newId = createdService?.id;

        // Step 2: If V2 Engine was enabled, configure routes immediately
        if (newId && (useV2Engine || processorRoutes.length > 0)) {
          await updateProduct(newId, {
            useV2Engine,
            processorRoutes,
            planId: formData.planId || processorRoutes[0]?.planId || '',
          });
        }

        toast.success(`Successfully created ${formData.name || formData.category}!`);
        if (newId) {
          navigate(`/dashboard/services/details/${newId}`);
        } else {
          navigate(`/dashboard/services/category/${selectedService.toLowerCase()}`);
        }
      } else {
        toast.error(createRes.data?.message || 'Failed to create product.');
      }
    } catch (err) {
      toast.error('An unexpected error occurred while creating the product.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingInitial) {
    return (
      <Page title="Create Product | Dashboard">
        <Container maxWidth="md" sx={{ py: 12 }}>
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Create New Product | Dashboard">
      <Container maxWidth="md" sx={{ py: 3, maxWidth: 880, mx: 'auto' }}>
        {/* Top Navigation Bar */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3.5}>
          <Box
            onClick={() =>
              navigate(
                isScopedService
                  ? `/dashboard/services/category/${selectedService.toLowerCase()}`
                  : '/dashboard/services'
              )
            }
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: '#0066FE',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': { opacity: 0.8 },
            }}
          >
            <ChevronLeft size={20} strokeWidth={2.5} />
            <span>
              {isScopedService
                ? `Back to ${activeServiceConfig.title}`
                : 'Back to services'}
            </span>
          </Box>

          <Stack direction="row" spacing={1.5}>
            <Button
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: '24px',
                bgcolor: '#F1F3F5',
                color: '#374151',
                px: 2.75,
                py: 0.9,
                fontSize: '0.875rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#E5E7EB', boxShadow: 'none' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateProduct}
              disabled={saving}
              sx={{
                borderRadius: '24px',
                bgcolor: '#0066FE',
                color: '#FFFFFF',
                px: 3.25,
                py: 0.9,
                fontSize: '0.875rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#0052CC', boxShadow: 'none' },
              }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : 'Create product'}
            </Button>
          </Stack>
        </Stack>

        {/* Page Header */}
        <Box mb={4}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: '#111827', fontSize: { xs: '1.75rem', sm: '2.25rem' }, mb: 0.75 }}
          >
            {isScopedService ? `Create ${activeServiceConfig.title} Product` : 'Create New Product'}
          </Typography>
          <Typography sx={{ color: '#6B7280', fontSize: '0.95rem' }}>
            {isScopedService
              ? `Configure pricing, wholesale routing, and automated failover for ${activeServiceConfig.title}.`
              : 'Add a new product or plan to your service catalog and configure automated V2 routing.'}
          </Typography>
        </Box>

        {/* 1. Service Type Selector */}
        <Card
          sx={{
            p: 3,
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            border: '1px solid #ECEEF1',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            mb: 3.5,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              1. Service Category
            </Typography>
            {isScopedService && (
              <Button
                size="small"
                onClick={() => setIsScopedService(false)}
                sx={{
                  fontSize: '0.75rem',
                  color: '#0066FE',
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#EFF6FF',
                  borderRadius: '16px',
                  px: 1.5,
                  py: 0.35,
                  '&:hover': { bgcolor: '#DBEAFE' },
                }}
              >
                Change Service Category
              </Button>
            )}
          </Stack>

          {isScopedService ? (
            /* Scoped single service view when clicked from Airtime (or any specific service) */
            <Box
              sx={{
                p: 2,
                borderRadius: '18px',
                border: `2px solid ${activeServiceConfig.color}`,
                bgcolor: activeServiceConfig.bg,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                minWidth: { xs: '100%', sm: 280 },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '12px',
                  bgcolor: '#FFFFFF',
                  color: activeServiceConfig.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                }}
              >
                {React.createElement(activeServiceConfig.icon, { size: 22 })}
              </Box>
              <div>
                <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: activeServiceConfig.color }}>
                  {activeServiceConfig.title}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#4B5563', fontWeight: 500 }}>
                  Active Service Category
                </Typography>
              </div>
            </Box>
          ) : (
            /* Full 5-service selector grid when unconstrained */
            <Grid container spacing={1.5}>
              {SERVICES_LIST.map((srv) => {
                const IconComp = srv.icon;
                const isSelected = selectedService === srv.key;
                return (
                  <Grid item xs={6} sm={4} md={2.4} key={srv.key}>
                    <Box
                      onClick={() => handleServiceChange(srv.key)}
                      sx={{
                        p: 2,
                        borderRadius: '18px',
                        border: isSelected ? `2px solid ${srv.color}` : '1px solid #E5E7EB',
                        bgcolor: isSelected ? srv.bg : '#FFFFFF',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                          borderColor: srv.color,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '12px',
                          bgcolor: isSelected ? '#FFFFFF' : srv.bg,
                          color: srv.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 1,
                        }}
                      >
                        <IconComp size={20} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 700,
                          color: isSelected ? srv.color : '#111827',
                        }}
                      >
                        {srv.title}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* If Data or Airtime Service is Selected: include Telecom Network Selection */}
          {(selectedService === 'Data' || selectedService === 'Airtime') && (
            <Box mt={3} pt={2.5} sx={{ borderTop: '1px dashed #E5E7EB' }}>
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: '#6B7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 1.5,
                }}
              >
                Telecom Network:
              </Typography>
              <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                {[
                  { key: 'MTN', name: 'MTN', color: '#EAB308', networkId: '1' },
                  { key: 'AIRTEL', name: 'Airtel', color: '#EF4444', networkId: '4' },
                  { key: 'GLO', name: 'Glo', color: '#22C55E', networkId: '2' },
                  { key: '9MOBILE', name: '9mobile', color: '#15803D', networkId: '3' },
                ].map((net) => {
                  const isNetSelected = selectedDataNetwork === net.key;
                  return (
                    <Button
                      key={net.key}
                      onClick={() => handleDataNetworkChange(net.key, net.networkId)}
                      startIcon={
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: net.color,
                          }}
                        />
                      }
                      sx={{
                        borderRadius: '20px',
                        px: 2.25,
                        py: 0.65,
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        boxShadow: 'none',
                        bgcolor: isNetSelected ? '#111827' : '#F3F4F6',
                        color: isNetSelected ? '#FFFFFF' : '#374151',
                        border: isNetSelected ? 'none' : '1px solid #E5E7EB',
                        '&:hover': {
                          bgcolor: isNetSelected ? '#1F2937' : '#E5E7EB',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {net.name}
                    </Button>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Card>



        {/* 2. Product Name & Network / Category */}
        <Card
          sx={{
            p: 3.5,
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            border: '1px solid #ECEEF1',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            mb: 3.5,
          }}
        >
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
            2. Product Information
          </Typography>

          <Stack spacing={3}>
            {/* Quick Category / Network Pills */}
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', mb: 1 }}>
                {selectedService === 'Data'
                  ? `Suggested ${selectedDataNetwork} Plan Categories`
                  : `Suggested ${selectedService === 'Airtime' ? 'Network' : 'Category / Provider'}`}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {visiblePresets.map((preset) => {
                  const isSelected = formData.category.toUpperCase() === preset.toUpperCase();
                  return (
                    <Button
                      key={preset}
                      onClick={() => {
                        const netMap = { MTN: '1', GLO: '2', '9MOBILE': '3', AIRTEL: '4' };
                        if (selectedService === 'Airtime') {
                          setSelectedDataNetwork(preset.toUpperCase());
                        }
                        setFormData((prev) => {
                          let suggestedName = prev.name;
                          let netId = prev.planNetworkId;
                          if (selectedService === 'Airtime') {
                            suggestedName = `${preset} Airtime VTU`;
                            netId = netMap[preset.toUpperCase()] || '1';
                          } else if (selectedService === 'Electricity') {
                            suggestedName = preset;
                          }
                          return {
                            ...prev,
                            category: preset,
                            name: suggestedName,
                            planNetworkId: netId,
                          };
                        });
                      }}
                      sx={{
                        borderRadius: '20px',
                        px: 2,
                        py: 0.5,
                        fontSize: '0.825rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: 'none',
                        bgcolor: isSelected ? '#111827' : '#F3F4F6',
                        color: isSelected ? '#FFFFFF' : '#374151',
                        border: isSelected ? 'none' : '1px solid #E5E7EB',
                        '&:hover': {
                          bgcolor: isSelected ? '#1F2937' : '#E5E7EB',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {preset}
                    </Button>
                  );
                })}
              </Stack>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Category / Network Name"
                  placeholder="e.g. MTN SME, AIRTEL CG, Jos Electricity"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  helperText="Primary classification group"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Product Plan Name"
                  placeholder="e.g. 20GB Monthly, 1.0GB SME, Jos Electricity"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  helperText="Display name shown on user dashboard and API"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Validity Duration"
                  placeholder="e.g. 30 Days, Instant, Monthly"
                  value={formData.validity}
                  onChange={(e) => setFormData({ ...formData, validity: e.target.value })}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description (Optional)"
              placeholder="e.g. Valid for 30 days. Usable on all internet-enabled devices."
              value={formData.descriptions}
              onChange={(e) => setFormData({ ...formData, descriptions: e.target.value })}
            />
          </Stack>
        </Card>

        {/* 3. Pricing & Margins Card */}
        <Card
          sx={{
            p: 3.5,
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            border: '1px solid #ECEEF1',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            mb: 3.5,
          }}
        >
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
            3. Pricing & Profit Margin
          </Typography>

          {isAirtime ? (
            <Stack spacing={3}>
              <Alert severity="info" sx={{ py: 0.5, borderRadius: '14px' }}>
                For <strong>Airtime VTU & Share</strong>, enter rate percentages (e.g. 2.0% customer discount, 3.5% wholesale commission).
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Retail User Discount (%)"
                    placeholder="2.00"
                    value={formData.discount}
                    onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Vendor Wholesale Margin (%)"
                    placeholder="3.50"
                    value={formData.buyingAmount}
                    onChange={(e) => setFormData({ ...formData, buyingAmount: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="VIP / Reseller Discount (%)"
                    placeholder="2.50"
                    value={formData.vipAmount}
                    onChange={(e) => setFormData({ ...formData, vipAmount: e.target.value })}
                  />
                </Grid>
              </Grid>

              {/* Live Airtime Margin Box */}
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: '16px',
                  bgcolor: airtimeMargin < 0 ? 'rgba(220, 38, 38, 0.035)' : 'rgba(22, 163, 74, 0.035)',
                  border: '1px solid',
                  borderColor: airtimeMargin < 0 ? 'rgba(220, 38, 38, 0.2)' : 'rgba(22, 163, 74, 0.2)',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
                  <div>
                    <Typography sx={{ color: '#4B5563', fontSize: '0.85rem', fontWeight: 650 }}>
                      Calculated Net Profit per Top-up
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '1.5rem',
                        fontWeight: 800,
                        color: airtimeMargin >= 0 ? '#16A34A' : '#DC2626',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {airtimeMargin >= 0 ? `+${airtimeMargin.toFixed(2)}%` : `${airtimeMargin.toFixed(2)}%`}
                    </Typography>
                    {vipAirtimeDiscount > 0 && (
                      <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', mt: 0.5 }}>
                        VIP Tier Net Margin ({vipAirtimeDiscount}% discount):{' '}
                        <strong style={{ color: airtimeVipMargin >= 0 ? '#16A34A' : '#DC2626' }}>
                          {airtimeVipMargin >= 0 ? `+${airtimeVipMargin.toFixed(2)}%` : `${airtimeVipMargin.toFixed(2)}%`}
                        </strong>
                      </Typography>
                    )}
                  </div>
                  <Chip
                    icon={airtimeMargin >= 0 ? <Sparkles size={14} /> : <AlertTriangle size={14} />}
                    label={airtimeMargin >= 0 ? 'Profitable' : 'Loss'}
                    color={airtimeMargin >= 0 ? 'success' : 'error'}
                    size="small"
                  />
                </Stack>
              </Box>
            </Stack>
          ) : (
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Retail Price (₦)"
                    placeholder="e.g. 13960"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Vendor Wholesale Cost (₦)"
                    placeholder="e.g. 11500"
                    value={formData.buyingAmount}
                    onChange={(e) => setFormData({ ...formData, buyingAmount: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="VIP Price (₦)"
                    placeholder="e.g. 13000"
                    value={formData.vipAmount}
                    onChange={(e) => setFormData({ ...formData, vipAmount: e.target.value })}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Cashback (₦)"
                    placeholder="0"
                    value={formData.cashback}
                    onChange={(e) => setFormData({ ...formData, cashback: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Referral Bonus (₦)"
                    placeholder="0"
                    value={formData.referralBonus}
                    onChange={(e) => setFormData({ ...formData, referralBonus: e.target.value })}
                  />
                </Grid>
              </Grid>

              {/* Live Cash Margin Box - Apple Inset Card with Real Unit Economics */}
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: '18px',
                  bgcolor: netMargin < 0 ? 'rgba(220, 38, 38, 0.035)' : 'rgba(22, 163, 74, 0.035)',
                  border: '1px solid',
                  borderColor: netMargin < 0 ? 'rgba(220, 38, 38, 0.2)' : 'rgba(22, 163, 74, 0.2)',
                  transition: 'all 0.2s ease',
                }}
              >
                {/* Top Row: Primary Metric & Status Badge */}
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1.5} mb={2}>
                  <div>
                    <Typography sx={{ color: '#4B5563', fontSize: '0.85rem', fontWeight: 650, letterSpacing: '-0.01em' }}>
                      Estimated Net Margin per Sale
                    </Typography>
                    <Stack direction="row" alignItems="baseline" spacing={1} mt={0.25}>
                      <Typography
                        sx={{
                          fontSize: '1.75rem',
                          fontWeight: 800,
                          color: netMargin >= 0 ? '#16A34A' : '#DC2626',
                          fontVariantNumeric: 'tabular-nums',
                          letterSpacing: '-0.02em',
                          lineHeight: 1.2,
                        }}
                      >
                        {netMargin >= 0 ? `+₦${netMargin.toLocaleString()}` : `-₦${Math.abs(netMargin).toLocaleString()}`}
                      </Typography>
                      {retailAmount > 0 && (
                        <Typography
                          sx={{
                            fontSize: '0.88rem',
                            fontWeight: 700,
                            color: netMargin >= 0 ? '#16A34A' : '#DC2626',
                          }}
                        >
                          ({netMargin >= 0 ? `+${netMarginPercent.toFixed(1)}%` : `${netMarginPercent.toFixed(1)}%`} margin)
                        </Typography>
                      )}
                    </Stack>
                    <Typography sx={{ fontSize: '0.72rem', color: '#6B7280', mt: 0.25 }}>
                      Net margin after deducting vendor wholesale cost, buyer cashback, and referral rewards.
                    </Typography>
                  </div>

                  <Chip
                    icon={netMargin >= 0 ? <Sparkles size={14} /> : <AlertTriangle size={14} />}
                    label={netMargin >= 0 ? 'Profitable' : 'Loss-Making'}
                    color={netMargin >= 0 ? 'success' : 'error'}
                    size="small"
                    sx={{ fontWeight: 700, px: 0.5 }}
                  />
                </Stack>

                {/* Apple Inset Breakdown Grid */}
                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ p: 1.25, borderRadius: '10px', bgcolor: '#FFFFFF', border: '1px solid rgba(0, 0, 0, 0.06)' }}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#6B7280', fontWeight: 600 }}>
                        Gross Spread
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 750, color: grossMargin >= 0 ? '#16A34A' : '#DC2626', fontVariantNumeric: 'tabular-nums' }}>
                        {grossMargin >= 0 ? `+₦${grossMargin.toLocaleString()}` : `-₦${Math.abs(grossMargin).toLocaleString()}`}
                      </Typography>
                      <Typography sx={{ fontSize: '0.625rem', color: '#9CA3AF' }}>
                        Retail − Wholesale
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Box sx={{ p: 1.25, borderRadius: '10px', bgcolor: '#FFFFFF', border: '1px solid rgba(0, 0, 0, 0.06)' }}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#6B7280', fontWeight: 600 }}>
                        Buyer Cashback
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 750, color: cashbackAmount > 0 ? '#DC2626' : '#6B7280', fontVariantNumeric: 'tabular-nums' }}>
                        {cashbackAmount > 0 ? `-₦${cashbackAmount.toLocaleString()}` : '₦0'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.625rem', color: '#9CA3AF' }}>
                        Wallet credit
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Box sx={{ p: 1.25, borderRadius: '10px', bgcolor: '#FFFFFF', border: '1px solid rgba(0, 0, 0, 0.06)' }}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#6B7280', fontWeight: 600 }}>
                        Referral Reward
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 750, color: referralAmount > 0 ? '#DC2626' : '#6B7280', fontVariantNumeric: 'tabular-nums' }}>
                        {referralAmount > 0 ? `-₦${referralAmount.toLocaleString()}` : '₦0'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.625rem', color: '#9CA3AF' }}>
                        Referrer credit
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Box sx={{ p: 1.25, borderRadius: '10px', bgcolor: '#FFFFFF', border: '1px solid rgba(0, 0, 0, 0.06)' }}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#6B7280', fontWeight: 600 }}>
                        Total Cost Outflow
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 750, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>
                        ₦{totalExpenses.toLocaleString()}
                      </Typography>
                      <Typography sx={{ fontSize: '0.625rem', color: '#9CA3AF' }}>
                        Wholesale + Rewards
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* VIP Price Margin Pill if set */}
                {hasVipPrice && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.25,
                      borderRadius: '10px',
                      bgcolor: '#FFFFFF',
                      border: '1px solid',
                      borderColor: vipNetMargin >= 0 ? 'rgba(22, 163, 74, 0.25)' : 'rgba(220, 38, 38, 0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>
                        VIP Tier Net Profit (₦{vipPrice.toLocaleString()} selling price):
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.88rem',
                          fontWeight: 800,
                          color: vipNetMargin >= 0 ? '#16A34A' : '#DC2626',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {vipNetMargin >= 0 ? `+₦${vipNetMargin.toLocaleString()}` : `-₦${Math.abs(vipNetMargin).toLocaleString()}`}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: vipNetMargin >= 0 ? '#16A34A' : '#DC2626' }}>
                        ({vipNetMargin >= 0 ? `+${vipNetMarginPercent.toFixed(1)}%` : `${vipNetMarginPercent.toFixed(1)}%`})
                      </Typography>
                    </Stack>

                    <Chip
                      label={vipNetMargin >= 0 ? 'VIP Profitable' : 'VIP Loss'}
                      color={vipNetMargin >= 0 ? 'success' : 'error'}
                      size="small"
                      sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }}
                    />
                  </Box>
                )}

                {/* Loss Warning Banner */}
                {netMargin < 0 && (
                  <Alert
                    severity="error"
                    icon={<AlertTriangle size={16} />}
                    sx={{
                      mt: 1.5,
                      py: 0.5,
                      borderRadius: '10px',
                      fontSize: '0.78rem',
                      bgcolor: 'rgba(220, 38, 38, 0.08)',
                      color: '#991B1B',
                      border: '1px solid rgba(220, 38, 38, 0.2)',
                    }}
                  >
                    <strong>Negative Margin Alert:</strong> Total expenses (Wholesale ₦{wholesaleCost.toLocaleString()} + Cashback ₦{cashbackAmount.toLocaleString()} + Referral ₦{referralAmount.toLocaleString()} = <strong>₦{totalExpenses.toLocaleString()}</strong>) exceed the retail price (<strong>₦{retailAmount.toLocaleString()}</strong>). You will lose <strong>₦{Math.abs(netMargin).toLocaleString()}</strong> on every retail order.
                  </Alert>
                )}
              </Box>
            </Stack>
          )}
        </Card>

        {/* 4. Multi-Processor Routing (V2 Engine) Card */}
        <MultiProcessorRoutingCard
          useV2Engine={useV2Engine}
          onChangeUseV2Engine={setUseV2Engine}
          processorRoutes={processorRoutes}
          onChangeRoutes={setProcessorRoutes}
          selectedService={selectedService}
          providers={providers}
          onSyncPlan={handleSyncPlan}
          onPrimaryPlanChange={(newPlanId) => {
            setFormData((prev) => ({ ...prev, planId: newPlanId }));
          }}
          onPrimaryNetworkChange={(newNetId) => {
            setFormData((prev) => ({ ...prev, planNetworkId: String(newNetId) }));
          }}
          fallbackPlanId={formData.planId}
          fallbackNetworkId={formData.planNetworkId}
          fallbackBuyingAmount={formData.buyingAmount}
        />

        {/* 5. Product Catalog Settings */}
        <Card
          sx={{
            p: 3.5,
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            border: '1px solid #ECEEF1',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            mb: 4,
          }}
        >
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
            5. Catalog Visibility
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.available}
                  onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                  color="success"
                />
              }
              label={
                <div>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    Available for Purchase
                  </Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.775rem' }}>
                    Allow users to purchase this product immediately
                  </Typography>
                </div>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.hidden}
                  onChange={(e) => setFormData({ ...formData, hidden: e.target.checked })}
                  color="warning"
                />
              }
              label={
                <div>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    Hide from App Catalog
                  </Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.775rem' }}>
                    Keep active on API but hide from mobile app
                  </Typography>
                </div>
              }
            />
          </Stack>
        </Card>

        {/* Bottom Action Buttons */}
        <Box display="flex" justifyContent="flex-end" gap={2} mb={6}>
          <Button
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: '24px',
              bgcolor: '#F1F3F5',
              color: '#374151',
              px: 3.5,
              py: 1.1,
              fontSize: '0.9rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#E5E7EB', boxShadow: 'none' },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleCreateProduct}
            disabled={saving}
            sx={{
              borderRadius: '24px',
              bgcolor: '#0066FE',
              color: '#FFFFFF',
              px: 4,
              py: 1.1,
              fontSize: '0.9rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0052CC', boxShadow: 'none' },
            }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Create product'}
          </Button>
        </Box>
      </Container>
    </Page>
  );
}
